import React from 'react';
import { Form, Select, Input, Button, Tooltip, Typography } from '../../../../components';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { FormIntroduction } from '../components/FormIntroduction';
import { InstanceTagsLabel } from '../components/InstanceTagsLabel';
import { LoadingState } from '../components/LoadingState';
import { CloudAttributesError } from '../components/CloudAttributesError';
import { CommonApplianceConfiguration } from '../components/CommonApplianceConfiguration';
import { VMBasedMigrationsConfig } from '../components/VMBasedMigrationsConfig';
import { useGCPApplianceSetup } from './useGCPApplianceSetup';
import { GCPNetworkConfig } from './GCPNetworkConfig';
import { KeyValuePairs } from '../components/KeyValuePairs';

const { Title } = Typography;

export const GCPApplianceSetup = ({ cloudAccount, onSubmit, onCancel, submitting, isRedeploy }) => {
    const {
        form,
        initialLoading,
        loadingError,
        loadCloudAttributes,
        loadingProjectData,
        loadingRegionData,
        project,
        region,
        instanceTags,
        setInstanceTags,
        networks,
        handleProjectChange,
        handleRegionChange,
        handleFormSubmit,
        projectOptions,
        regionOptions,
        zoneOptions,
        commonConfigProps,
    } = useGCPApplianceSetup({ cloudAccount, onSubmit, isRedeploy });

    if (initialLoading) {
        return <LoadingState />;
    }

    if (loadingError) {
        return (
            <div className="gcp-appliance-setup">
                <FormIntroduction />
                <CloudAttributesError error={loadingError} onRetry={loadCloudAttributes} />
            </div>
        );
    }

    return (
        <div className="gcp-appliance-setup">
            <FormIntroduction />
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFormSubmit}
                initialValues={{ machineType: 'e2-medium', diskType: 'pd-standard', assignPublicIp: 'none', useProxyEnabled: true }}
            >
                <Title level={4}>GCP Account Setup</Title>

                <Form.Item
                    label="Project"
                    name="project"
                    rules={[{ required: true, message: 'Please select a project' }]}
                >
                    <Select
                        placeholder="Select a Project"
                        onChange={handleProjectChange}
                        loading={loadingProjectData}
                        showSearch={{ optionFilterProp: 'label' }}
                        options={projectOptions}
                    />
                </Form.Item>

                <Form.Item label="Region" name="region" rules={[{ required: true, message: 'Please select a region' }]}>
                    <Select
                        placeholder="Select a Region"
                        onChange={handleRegionChange}
                        disabled={!project || loadingProjectData}
                        loading={loadingRegionData}
                        showSearch={{ optionFilterProp: 'label' }}
                        options={regionOptions}
                    />
                </Form.Item>

                {loadingRegionData && <div className="loading-indicator">Loading region data...</div>}

                <Form.Item label="Zone" name="zone" rules={[{ required: true, message: 'Please select a zone' }]}>
                    <Select
                        placeholder="Select a Zone"
                        disabled={!region || loadingRegionData}
                        showSearch={{ optionFilterProp: 'label' }}
                        options={zoneOptions}
                    />
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            Machine Type{' '}
                            <Tooltip title="This is the Machine Type used for the Migration Appliance. Prior to migration, you will be able to specify the machine type for your migration.">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                    name="machineType"
                >
                    <Select disabled options={[{ value: 'e2-medium', label: 'e2-medium' }]} />
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            Disk Type{' '}
                            <Tooltip title="This is the Disk Type used for the Migration Appliance. Prior to migration, you will be able to specify the disk type for your migration.">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                    name="diskType"
                    rules={[{ required: true, message: 'Please select a disk type' }]}
                >
                    <Select
                        options={[
                            { value: 'pd-standard', label: 'Standard Persistent' },
                            { value: 'pd-balanced', label: 'Balanced Persistent' },
                            { value: 'pd-ssd', label: 'SSD Persistent' },
                        ]}
                    />
                </Form.Item>

                <GCPNetworkConfig networks={networks} disabled={!region || loadingRegionData} />

                <Form.Item
                    label={
                        <span>
                            VM Name{' '}
                            <Tooltip title="VM Name of the Migration Appliance. If not specified, it will be auto-generated. Must contain lowercase characters, numbers, and hyphens, and start with a lowercase character.">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                    name="vmName"
                    rules={[
                        {
                            pattern: /^[a-z]([-a-z0-9]*[a-z0-9])?$/,
                            message:
                                'VM Name should contain lowercase characters, numbers, and hyphens, and start with a lowercase character.',
                        },
                    ]}
                >
                    <Input placeholder="Enter a VM Name" />
                </Form.Item>

                <Form.Item
                    label={<InstanceTagsLabel />}
                >
                    <KeyValuePairs
                        dataSource={instanceTags}
                        onChange={setInstanceTags}
                        allowEmptyValue={true}
                        keyPlaceholder="key"
                        valuePlaceholder="value"
                        instructionMessage="Click the + icon or press Enter to add a tag. You can add up to 47 tags (3 tags — BuildNumber, Name and RiverMeadowId — will be added automatically)."
                        limit={50}
                        keyValidation={{ maxLength: 127 }}
                        valueValidation={{ maxLength: 255 }}
                    />
                </Form.Item>

                <VMBasedMigrationsConfig />

                <CommonApplianceConfiguration {...commonConfigProps} />

                <div className="modal-footer">
                    <Button type="default" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={submitting}>
                        Initiate Migration Appliance checks
                    </Button>
                </div>
            </Form>
        </div>
    );
};
